/**
 * Filingramp login (OAuth2 "password" grant), mirroring ramp-web-app's
 * `signIn` (clients/ramp-web-app/src/actions/authActions.js):
 *   POST {API}/auth/authentication/local
 *   Content-Type: application/x-www-form-urlencoded
 *   Authorization: Basic <public web client credential>
 *   body: username=<email>&password=<password>
 *   -> { accessToken, expiresIn, issuedAt, scope, user: { id } }
 *
 * The access token is stored raw (no "Bearer " prefix) — the agent socket sends
 * it as the Authorization header exactly like the web app.
 */

import { apiBaseUrl as API_BASE } from '@/shared/config/endpoints'

// Public OAuth client credential embedded in ramp-web-app's bundle (same posture).
// Overridable via env if a different client is provisioned for the add-in.
const CLIENT_BASIC =
  import.meta.env.VITE_AUTH_CLIENT_BASIC ||
  'NXJxaDFkcXZrN3JmdDdpYjZlaDBhbHY1bXUydjRmOkpKdW00NldvV2NNeW5vVTFDV2xraEw='

const STORAGE = {
  token: 'token',
  userId: 'userId',
  scope: 'scope',
  issuedAt: 'issuedAt',
  expiresIn: 'expiresIn'
} as const

export interface AuthState {
  token: string
  userId?: string
  scope?: string
  /** Epoch ms when the token expires (issuedAt + expiresIn). */
  expiresAt?: number
}

interface LocalAuthResponse {
  accessToken: string
  expiresIn?: number | string
  issuedAt?: number | string
  scope?: string
  user?: { id?: string }
}

/** Account auth types returned by /users/account/auth-type (mirrors ramp-web-app). */
export type AuthType = 'RAMP' | 'AZURE_AD' | 'SAML' | string

/**
 * Determine how an account authenticates (so the UI can show a password field for
 * RAMP accounts or route SSO accounts). Mirrors web-app's `getUserAuthType`:
 *   GET {API}/users/account/auth-type?username=<email>  -> "RAMP" | "AZURE_AD" | "SAML"
 */
export async function getAuthType(email: string): Promise<AuthType> {
  const res = await fetch(
    `${API_BASE}/users/account/auth-type?username=${encodeURIComponent(email)}`,
    { headers: { Accept: 'application/json' } }
  )
  if (!res.ok) throw new Error(await safeErrorMessage(res))

  const raw = (await res.text()).trim()
  let data: unknown = raw
  try {
    data = JSON.parse(raw)
  } catch {
    /* plain string body */
  }
  if (typeof data === 'string') return data.replace(/^"|"$/g, '')
  const obj = data as { type?: string; authType?: string }
  return obj?.type ?? obj?.authType ?? ''
}

/** Validate an email address (same regex as ramp-web-app's isValidEmail). */
export function isValidEmail(email: string): boolean {
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email)
}

export async function login(email: string, password: string): Promise<AuthState> {
  const body = new URLSearchParams({ username: email, password })
  const res = await fetch(`${API_BASE}/auth/authentication/local`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${CLIENT_BASIC}`
    },
    body: body.toString()
  })

  if (!res.ok) {
    const message = await safeErrorMessage(res)
    throw new Error(message)
  }

  const data = (await res.json()) as LocalAuthResponse
  if (!data.accessToken) throw new Error('Login succeeded but no access token was returned.')
  return finalizeAuth(data)
}

/**
 * Store an auth payload obtained out-of-band (e.g. from the SSO dialog) and return
 * the resulting state. Shape matches the `{ accessToken, user, scope, issuedAt,
 * expiresIn }` payload ramp-web-app's Azure/SAML handlers consume.
 */
export function persistSsoAuth(payload: LocalAuthResponse): AuthState {
  if (!payload?.accessToken) throw new Error('SSO returned no access token.')
  return finalizeAuth(payload)
}

/** Build AuthState from an auth response and persist it to localStorage. */
function finalizeAuth(data: LocalAuthResponse): AuthState {
  const issuedAt = Number(data.issuedAt) || Date.now()
  const expiresInMs = Number(data.expiresIn) ? Number(data.expiresIn) * 1000 : undefined
  const state: AuthState = {
    token: data.accessToken,
    userId: data.user?.id,
    scope: data.scope,
    expiresAt: expiresInMs ? issuedAt + expiresInMs : undefined
  }
  persist(state, issuedAt, data.expiresIn)
  return state
}

export function logout(): void {
  Object.values(STORAGE).forEach((key) => window.localStorage.removeItem(key))
}

/** Returns a valid (non-expired) stored auth state, or null. */
export function getStoredAuth(): AuthState | null {
  const token = window.localStorage.getItem(STORAGE.token)
  if (!token) return null

  const issuedAt = Number(window.localStorage.getItem(STORAGE.issuedAt))
  const expiresIn = Number(window.localStorage.getItem(STORAGE.expiresIn))
  const expiresAt = issuedAt && expiresIn ? issuedAt + expiresIn * 1000 : undefined

  if (expiresAt && Date.now() >= expiresAt) {
    logout()
    return null
  }

  return {
    token,
    userId: window.localStorage.getItem(STORAGE.userId) || undefined,
    scope: window.localStorage.getItem(STORAGE.scope) || undefined,
    expiresAt
  }
}

function persist(state: AuthState, issuedAt: number, expiresIn?: number | string): void {
  window.localStorage.setItem(STORAGE.token, state.token)
  if (state.userId) window.localStorage.setItem(STORAGE.userId, state.userId)
  if (state.scope) window.localStorage.setItem(STORAGE.scope, state.scope)
  window.localStorage.setItem(STORAGE.issuedAt, String(issuedAt))
  if (expiresIn != null) window.localStorage.setItem(STORAGE.expiresIn, String(expiresIn))
}

async function safeErrorMessage(res: Response): Promise<string> {
  try {
    const data = await res.json()
    return data?.message || data?.error_description || `Login failed (${res.status}).`
  } catch {
    return res.status === 401 ? 'Invalid email or password.' : `Login failed (${res.status}).`
  }
}
